import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft, RefreshCw, Plus, Link2, Copy, UserMinus, Trash2,
  CheckCircle2, XCircle, BarChart3, Send,
} from 'lucide-react';
import { toast } from 'sonner';

function AdminAnalyticsViewers({ user, onLogout }) {
  const { lang } = useParams();
  const currentLang = lang || 'ru';
  const navigate = useNavigate();

  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [linkDialog, setLinkDialog] = useState(null); // { name, bind_link, bot }
  const [hook, setHook] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics-viewers`, { headers: getAuthHeaders() });
      setViewers(res.data);
    } catch (e) {
      toast.error('Не удалось загрузить список');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!name.trim()) { toast.error('Введите имя'); return; }
    try {
      await axios.post(`${API}/admin/analytics-viewers`, { name: name.trim(), note: note.trim() || null }, { headers: getAuthHeaders() });
      toast.success('Зритель создан');
      setShowCreate(false); setName(''); setNote('');
      load();
    } catch (e) { toast.error('Ошибка создания'); }
  };

  const genLink = async (v) => {
    try {
      const res = await axios.post(`${API}/admin/analytics-viewers/${v.id}/bind-link`, {}, { headers: getAuthHeaders() });
      setLinkDialog({ name: v.name, ...res.data });
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка генерации ссылки'); }
  };

  const unbind = async (v) => {
    if (!window.confirm(`Отвязать Telegram у «${v.name}»?`)) return;
    try {
      await axios.post(`${API}/admin/analytics-viewers/${v.id}/unbind`, {}, { headers: getAuthHeaders() });
      toast.success('Отвязано'); load();
    } catch (e) { toast.error('Ошибка'); }
  };

  const toggle = async (v) => {
    try {
      await axios.patch(`${API}/admin/analytics-viewers/${v.id}/toggle`, {}, { headers: getAuthHeaders() });
      load();
    } catch (e) { toast.error('Ошибка'); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Удалить зрителя «${v.name}»?`)) return;
    try {
      await axios.delete(`${API}/admin/analytics-viewers/${v.id}`, { headers: getAuthHeaders() });
      toast.success('Удалено'); load();
    } catch (e) { toast.error('Ошибка'); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success('Скопировано'); };

  const checkHook = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics-viewers/webhook/status`, { headers: getAuthHeaders() });
      setHook(res.data?.result || res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Токен бота не настроен'); }
  };

  const setHookUrl = async () => {
    const url = `${window.location.origin}/api/analytics-app/telegram-webhook`;
    try {
      await axios.post(`${API}/admin/analytics-viewers/webhook/set`, { url }, { headers: getAuthHeaders() });
      toast.success('Webhook установлен'); checkHook();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка установки webhook'); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-analytics-viewers">
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/${currentLang}/admin`)} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-600" /> Доступ к аналитике
              </h1>
              <p className="text-sm text-gray-500">Кто может открывать мини-приложение аналитики в Telegram</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Webhook setup */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4" /> Бот аналитики</CardTitle>
            <CardDescription>Один раз настройте webhook для бота, чтобы привязка работала.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={checkHook} data-testid="hook-status-btn">Проверить статус</Button>
            <Button size="sm" onClick={setHookUrl} data-testid="hook-set-btn">Установить webhook</Button>
            {hook && (
              <span className="text-xs text-gray-600 ml-2 break-all">
                URL: {hook.url || '—'} · ожидает: {hook.pending_update_count ?? 0}
              </span>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-between items-center">
          <h2 className="text-base font-semibold text-gray-700">Зрители ({viewers.length})</h2>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="add-viewer-btn">
            <Plus className="w-4 h-4 mr-1" /> Добавить
          </Button>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-10">Загрузка…</div>
        ) : viewers.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-gray-400">
            Пока нет зрителей. Нажмите «Добавить», создайте человека и отправьте ему ссылку-привязку.
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {viewers.map((v) => (
              <Card key={v.id} data-testid={`viewer-${v.id}`} className={v.is_active ? '' : 'opacity-60'}>
                <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-[180px]">
                    <div className="font-semibold flex items-center gap-2">
                      {v.name}
                      {v.is_bound
                        ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300"><CheckCircle2 className="w-3 h-3 mr-1" />Привязан</Badge>
                        : <Badge variant="outline" className="text-gray-500"><XCircle className="w-3 h-3 mr-1" />Не привязан</Badge>}
                      {!v.is_active && <Badge variant="outline" className="text-red-500">Выключен</Badge>}
                    </div>
                    {v.note && <div className="text-xs text-gray-500 mt-0.5">{v.note}</div>}
                    {v.is_bound && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        TG: {v.telegram_username ? '@' + v.telegram_username : v.telegram_first_name || v.telegram_user_id}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => genLink(v)} data-testid={`link-${v.id}`}>
                      <Link2 className="w-4 h-4 mr-1" /> Ссылка-привязка
                    </Button>
                    {v.is_bound && (
                      <Button variant="outline" size="sm" onClick={() => unbind(v)} data-testid={`unbind-${v.id}`}>
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => toggle(v)} data-testid={`toggle-${v.id}`}>
                      {v.is_active ? 'Выкл' : 'Вкл'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(v)} className="text-red-600" data-testid={`delete-${v.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Новый зритель</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Имя</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Напр. Гульсим (фин.)" data-testid="viewer-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Заметка (необязательно)</label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Роль / отдел" data-testid="viewer-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Отмена</Button>
            <Button onClick={create} data-testid="viewer-create-submit">Создать</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bind link dialog */}
      <Dialog open={!!linkDialog} onOpenChange={() => setLinkDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ссылка-привязка для «{linkDialog?.name}»</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">
            Отправьте эту ссылку человеку. Он откроет её один раз в Telegram — и получит доступ к аналитике.
          </p>
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
            <code className="text-xs break-all flex-1" data-testid="bind-link-text">{linkDialog?.bind_link}</code>
            <Button size="sm" variant="outline" onClick={() => copy(linkDialog.bind_link)} data-testid="copy-link-btn">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setLinkDialog(null)}>Готово</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminAnalyticsViewers;
